/* eslint-disable no-underscore-dangle */
import React from 'react';
import PropTypes from 'prop-types';
import DayPicker, { LocaleUtils } from 'react-day-picker';
import { getUuid } from '@front10/helpers/dist/uuid';
import createStyles from '@front10/helpers/dist/UseStyles';
import { Context } from '@front10/helpers/dist/Context';
import DatePickerCaption from './DatePickerCaption';
import DatePickerNav from './DatePickerNav';
import DatePickerWeekday from './DatePickerWeekday';
import DatePickerDay from './DatePickerDay';
import {
  fullMonths,
  weekDaysShort as weekDaysShortMonths,
  weekDaysLong as weekDaysLongMonths
} from '../../../helpers/localization/localizationUtils';

class DatePicker extends React.Component {
  constructor(props) {
    super(props);
    this.id = getUuid();
    this.months = [];
    this.weekDaysShort = [];
    this.weekdaysLong = [];
  }

  componentDidMount() {
    const { translate, localizationData } = this.context;
    const { months, weekdaysLong, weekDaysShort } = this.props;
    this.months = months || fullMonths(translate, localizationData);
    this.weekDaysShort = weekDaysShort || weekDaysShortMonths(translate, localizationData);
    this.weekdaysLong = weekdaysLong || weekDaysLongMonths(translate, localizationData);
    this.removeTabIndexFromCalendar();
  }

  componentDidUpdate(prevProps) {
    if (prevProps !== this.props) {
      this.removeTabIndexFromCalendar();
    }
  }

  removeTabIndexFromCalendar = () => {
    // TODO: Remove tabIndex not working.
    // http://react-day-picker.js.org/api/DayPicker/#tabIndex
    if (typeof document !== 'undefined') {
      const firstDay = document.querySelector(
        `.fr-date-picker-${this.id} .DayPicker-Day:not(.DayPicker-Day--outside)`
      );
      if (firstDay) {
        firstDay.removeAttribute('tabindex');
        // firstDay.setAttribute('tabindex', '-1');
      }
    }
  };

  enableAriaHiddenDaysOutside = () => {
    if (typeof document !== 'undefined') {
      const outsides = document.querySelectorAll('.DayPicker-Day--outside');
      outsides.forEach((e) => e.setAttribute('aria-hidden', 'true'));
    }
  };

  captionElement = ({ date, localizationUtils, locale }) => {
    const { captionElement, captionItem } = this.props;
    if (captionElement && typeof captionElement === 'function') {
      return captionElement({ date, localizationUtils, locale });
    }
    return (
      <DatePickerCaption
        {...captionItem}
        date={date}
        localizationUtils={localizationUtils}
        locale={locale}
      />
    );
  };

  navbarElement = ({
    nextMonth,
    previousMonth,
    onPreviousClick,
    onNextClick,
    localizationUtils
  }) => {
    const { navbarElement, navColor, navigationItem } = this.props;
    if (navbarElement && typeof navbarElement === 'function') {
      return navbarElement({
        nextMonth,
        previousMonth,
        onPreviousClick,
        onNextClick,
        localizationUtils
      });
    }
    return (
      <DatePickerNav
        {...navigationItem}
        nextMonth={nextMonth}
        previousMonth={previousMonth}
        onPreviousClick={onPreviousClick}
        onNextClick={onNextClick}
        localizationUtils={localizationUtils}
        color={navColor}
      />
    );
  };

  weekdayElement = ({ weekday, localizationUtils, locale }) => {
    const { weekdayElement, weekdayItem } = this.props;
    if (weekdayElement && typeof weekdayElement === 'function') {
      return weekdayElement({ weekday, localizationUtils, locale });
    }
    return <DatePickerWeekday {...weekdayItem} weekday={weekday} />;
  };

  dayElement = (day) => {
    const { modifiers, dayItems, renderDay, firstDayOfWeek } = this.props;
    const { localizationData } = this.context;
    let { modifiersColors } = this.props;
    modifiersColors = modifiersColors || {};
    if (renderDay && typeof renderDay === 'function') {
      const dow =
        localizationData && localizationData._week && localizationData._week.dow
          ? localizationData._week.dow
          : firstDayOfWeek;
      const lastDayOfWeek = dow === 0 ? 6 : dow - 1;
      return renderDay(day, day.getDay() === dow, day.getDay() === lastDayOfWeek);
    }
    let props = Object.assign({}, dayItems);

    Object.keys(modifiersColors).every((i) => {
      if (modifiers[i] && typeof modifiers[i] === 'function' && modifiers[i](day)) {
        props = Object.assign(props, modifiersColors[i]);
        return false;
      }
      return true;
    });
    return <DatePickerDay day={day} {...props} />;
  };

  render = () => {
    const { firstDayOfWeek, formatDay } = this.props;
    const { direction, localizationData, theme } = this.context;
    const [className] = createStyles(this.props, { direction, theme });
    const dtpClassName = `${className}  front10-date-selector fr-w-100 fr-date-picker-${this.id}`;
    const dow =
      localizationData && localizationData._week && localizationData._week.dow
        ? localizationData._week.dow
        : firstDayOfWeek;
    return (
      <div dir={direction}>
        <DayPicker
          {...this.props}
          localizationUtils={{
            ...LocaleUtils,
            formatDay: (day, locale) => {
              if (typeof formatDay === 'function') {
                return formatDay(day, locale);
              }
              return null;
            }
          }}
          className={dtpClassName}
          captionElement={this.captionElement}
          navbarElement={this.navbarElement}
          weekdayElement={this.weekdayElement}
          formatDay={this.formatDay}
          renderDay={this.dayElement}
          firstDayOfWeek={dow}
        />
      </div>
    );
  };
}

DatePicker.propTypes = {
  className: PropTypes.string,
  fontSize: PropTypes.string,
  /**
   * Color of navigation buttons
   */
  navColor: PropTypes.string,
  /**
   * Settings for caption
   */
  captionItem: PropTypes.objectOf(PropTypes.any),
  /**
   * Settings for navigation
   */
  navigationItem: PropTypes.objectOf(PropTypes.any),
  /**
   * Settings for weekdays
   */
  weekdayItem: PropTypes.objectOf(PropTypes.any),
  /**
   * Settings for days
   */
  dayItems: PropTypes.objectOf(PropTypes.any),
  months: PropTypes.arrayOf(PropTypes.string),
  weekdaysShort: PropTypes.arrayOf(PropTypes.string),
  weekdaysLong: PropTypes.arrayOf(PropTypes.string),
  disabledDays: PropTypes.arrayOf(PropTypes.any),
  captionElement: PropTypes.func,
  navbarElement: PropTypes.func,
  weekdayElement: PropTypes.func,
  modifiers: PropTypes.objectOf(PropTypes.any),
  renderDay: PropTypes.func,
  modifiersColors: PropTypes.objectOf(PropTypes.any),
  wcagWeekDayFormat: PropTypes.string,
  wcagMonthFormat: PropTypes.string,
  weekDaysShort: PropTypes.arrayOf(PropTypes.string),
  /**
   * First Day Of the Week
   */
  firstDayOfWeek: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6]),
  /**
   * Format Day function
   */
  formatDay: PropTypes.func
};

DatePicker.defaultProps = {
  className: '',
  fontSize: '2',
  navColor: 'link',
  captionItem: {},
  navigationItem: {},
  weekdayItem: {},
  dayItems: {},
  months: null,
  weekdaysShort: null,
  weekdaysLong: null,
  disabledDays: [],
  captionElement: null,
  navbarElement: null,
  weekdayElement: null,
  modifiers: {},
  renderDay: null,
  modifiersColors: {},
  wcagWeekDayFormat: 'long',
  wcagMonthFormat: 'long',
  weekDaysShort: null,
  firstDayOfWeek: 0,
  formatDay: null
};

DatePicker.contextType = Context;

export default DatePicker;
